import React, { useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { DS } from '../../PatientForms/designTokens';
import { PreviousPreventiveMedication } from '../types';

interface MultiplePreviousPreventiveMedicationsProps {
  previousPreventiveMedications: PreviousPreventiveMedication[];
  setPreviousPreventiveMedications: (
    medications: PreviousPreventiveMedication[]
  ) => void;
  handleFormSubmit: (e: React.FormEvent) => void;
}

const MultiplePreviousPreventiveMedications: React.FC<
  MultiplePreviousPreventiveMedicationsProps
> = ({
  previousPreventiveMedications,
  setPreviousPreventiveMedications,
  handleFormSubmit,
}) => {
  const handleInputChange = (
    index: number,
    field: keyof PreviousPreventiveMedication,
    value: string
  ) => {
    const updatedMedications = [...previousPreventiveMedications];
    updatedMedications[index][field] = value;
    setPreviousPreventiveMedications(updatedMedications);
  };

  const handleAddNewSet = () => {
    setPreviousPreventiveMedications([
      ...previousPreventiveMedications,
      {
        name: '',
        dose: '',
        startYear: '',
        stopReason: '',
        sideEffects: '',
        stopReasonDetails: '',
        stopYear: '',
      },
    ]);
  };

  const [addMoreMode, setAddMoreMode] = useState<null | boolean>(null);
  const [shouldSubmit, setShouldSubmit] = useState(false);

  const handleAddMoreRadioChange = (value: 'ja' | 'nej') => {
    if (value === 'ja') {
      handleAddNewSet();
      setAddMoreMode(true);
    } else {
      setAddMoreMode(false);
      setPreviousPreventiveMedications(
        previousPreventiveMedications.slice(0, 1)
      );
    }
    setTimeout(() => {
      handleFormSubmit(new Event('submit') as unknown as React.FormEvent);
    }, 2000);
  };

  useEffect(() => {
    if (shouldSubmit) {
      handleFormSubmit(new Event('submit') as unknown as React.FormEvent);
      setShouldSubmit(false);
    }
  }, [shouldSubmit, previousPreventiveMedications, handleFormSubmit]);

  useEffect(() => {
    if (previousPreventiveMedications.length > 1 && addMoreMode !== true) {
      setAddMoreMode(true);
    }
  }, [previousPreventiveMedications, addMoreMode]);

  if (!previousPreventiveMedications) {
    return null;
  }

  return (
    <Box>
      {previousPreventiveMedications.map((medication, index) => (
        // eslint-disable-next-line react/no-array-index-key -- entries have no id; the list is only appended to, edited in place or truncated from the end
        <Box key={index}>
          {index > 0 && (
            <Typography
              sx={{
                fontWeight: 600,
                color: '#374151',
                mt: 2,
                mb: 1,
                fontFamily: DS.fontBody,
                fontSize: '16px',
              }}
            >
              Förebyggande läkemedel {index + 1}
            </Typography>
          )}
          <Box sx={{ mb: 2 }}>
            <Box sx={{ mb: 2 }}>
              <Typography
                sx={{
                  display: 'block',
                  color: '#374151',
                  mb: 1,
                  fontFamily: DS.fontBody,
                  fontSize: '16px',
                }}
              >
                Vilket läkemedel?
              </Typography>
              <input
                type="text"
                onBlur={handleFormSubmit}
                value={medication.name}
                onChange={(e) =>
                  handleInputChange(index, 'name', e.target.value)
                }
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #D1D5DB',
                  borderRadius: '6px',
                  fontSize: '16px',
                  fontFamily: DS.fontBody,
                }}
              />
            </Box>
            <Box sx={{ mb: 2 }}>
              <Typography
                sx={{
                  display: 'block',
                  color: '#374151',
                  mb: 1,
                  fontFamily: DS.fontBody,
                  fontSize: '16px',
                }}
              >
                Vilken dos?
              </Typography>
              <input
                type="text"
                onBlur={handleFormSubmit}
                value={medication.dose}
                onChange={(e) =>
                  handleInputChange(index, 'dose', e.target.value)
                }
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #D1D5DB',
                  borderRadius: '6px',
                  fontSize: '16px',
                  fontFamily: DS.fontBody,
                }}
              />
            </Box>
          </Box>

          <Box sx={{ mb: 2 }}>
            <Typography
              sx={{
                display: 'block',
                color: '#374151',
                mb: 1,
                fontFamily: DS.fontBody,
                fontSize: '16px',
              }}
            >
              Startår:
            </Typography>
            <input
              type="text"
              onBlur={handleFormSubmit}
              value={medication.startYear}
              onChange={(e) =>
                handleInputChange(index, 'startYear', e.target.value)
              }
              style={{
                width: '100%',
                padding: '8px 12px',
                border: '1px solid #D1D5DB',
                borderRadius: '6px',
                fontSize: '16px',
                fontFamily: DS.fontBody,
              }}
            />
          </Box>
          <Box sx={{ mb: 2 }}>
            <Typography
              sx={{
                display: 'block',
                color: '#374151',
                mb: 1,
                fontFamily: DS.fontBody,
                fontSize: '16px',
              }}
            >
              Varför slutade du med det?
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Box
                component="label"
                sx={{ display: 'flex', alignItems: 'center' }}
              >
                <input
                  type="radio"
                  onBlur={handleFormSubmit}
                  value="Bristande effekt"
                  checked={medication.stopReason === 'Bristande effekt'}
                  onChange={(e) =>
                    handleInputChange(index, 'stopReason', e.target.value)
                  }
                  style={{ marginRight: '8px' }}
                />
                Bristande effekt
              </Box>
              <Box
                component="label"
                sx={{ display: 'flex', alignItems: 'center' }}
              >
                <input
                  type="radio"
                  onBlur={handleFormSubmit}
                  value="Biverkan"
                  checked={medication.stopReason === 'Biverkan'}
                  onChange={(e) =>
                    handleInputChange(index, 'stopReason', e.target.value)
                  }
                  style={{ marginRight: '8px' }}
                />
                Biverkan
              </Box>
              <Box
                component="label"
                sx={{ display: 'flex', alignItems: 'center' }}
              >
                <input
                  type="radio"
                  onBlur={handleFormSubmit}
                  value="Annat skäl"
                  checked={medication.stopReason === 'Annat skäl'}
                  onChange={(e) =>
                    handleInputChange(index, 'stopReason', e.target.value)
                  }
                  style={{ marginRight: '8px' }}
                />
                Annat skäl
              </Box>
            </Box>
          </Box>
          {medication.stopReason === 'Bristande effekt' && (
            <Box sx={{ mt: 2 }}>
              <Typography
                sx={{
                  display: 'block',
                  color: '#374151',
                  mb: 1,
                  fontFamily: DS.fontBody,
                  fontSize: '16px',
                }}
              >
                Stoppår:
              </Typography>
              <input
                type="text"
                onBlur={handleFormSubmit}
                value={medication.stopYear}
                onChange={(e) =>
                  handleInputChange(index, 'stopYear', e.target.value)
                }
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #D1D5DB',
                  borderRadius: '6px',
                  fontSize: '16px',
                  fontFamily: DS.fontBody,
                }}
              />
            </Box>
          )}
          {medication.stopReason === 'Biverkan' && (
            <Box sx={{ mt: 2 }}>
              <Typography
                sx={{
                  display: 'block',
                  color: '#374151',
                  fontFamily: DS.fontBody,
                  fontSize: '16px',
                }}
              >
                Ange biverkan: (max 10 ord)
              </Typography>
              <input
                type="text"
                onBlur={handleFormSubmit}
                value={medication.sideEffects}
                onChange={(e) =>
                  handleInputChange(index, 'sideEffects', e.target.value)
                }
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #D1D5DB',
                  borderRadius: '6px',
                  fontSize: '16px',
                  fontFamily: DS.fontBody,
                  marginBottom: '8px',
                }}
              />
              <Typography
                sx={{
                  display: 'block',
                  color: '#374151',
                  mb: 1,
                  fontFamily: DS.fontBody,
                  fontSize: '16px',
                }}
              >
                Stoppår:
              </Typography>
              <input
                type="text"
                onBlur={handleFormSubmit}
                value={medication.stopYear}
                onChange={(e) =>
                  handleInputChange(index, 'stopYear', e.target.value)
                }
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #D1D5DB',
                  borderRadius: '6px',
                  fontSize: '16px',
                  fontFamily: DS.fontBody,
                }}
              />
            </Box>
          )}
          {medication.stopReason === 'Annat skäl' && (
            <Box sx={{ mt: 2 }}>
              <Typography
                sx={{
                  display: 'block',
                  color: '#374151',
                  mb: 1,
                  fontFamily: DS.fontBody,
                  fontSize: '16px',
                }}
              >
                Ange skäl till avslut: (max 10 ord)
              </Typography>
              <input
                type="text"
                onBlur={handleFormSubmit}
                value={medication.stopReasonDetails}
                onChange={(e) =>
                  handleInputChange(index, 'stopReasonDetails', e.target.value)
                }
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #D1D5DB',
                  borderRadius: '6px',
                  fontSize: '16px',
                  fontFamily: DS.fontBody,
                  marginBottom: '8px',
                }}
              />
              <Typography
                sx={{
                  display: 'block',
                  color: '#374151',
                  mb: 1,
                  fontFamily: DS.fontBody,
                  fontSize: '16px',
                }}
              >
                Stoppår:
              </Typography>
              <input
                type="text"
                onBlur={handleFormSubmit}
                value={medication.stopYear}
                onChange={(e) =>
                  handleInputChange(index, 'stopYear', e.target.value)
                }
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #D1D5DB',
                  borderRadius: '6px',
                  fontSize: '16px',
                  fontFamily: DS.fontBody,
                }}
              />
            </Box>
          )}

          {index === 0 && (
            <>
              <Typography
                sx={{
                  fontWeight: 600,
                  color: '#374151',
                  mt: 2,
                  mb: 1,
                  fontFamily: DS.fontBody,
                  fontSize: '16px',
                }}
              >
                Har du provat något annat förebyggande läkemedel än det du just
                uppgav?
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                <Box
                  component="label"
                  sx={{ display: 'flex', alignItems: 'center' }}
                >
                  <input
                    type="radio"
                    onBlur={handleFormSubmit}
                    name="addMore"
                    value="ja"
                    checked={addMoreMode === true}
                    onChange={() => handleAddMoreRadioChange('ja')}
                    style={{ marginRight: '8px' }}
                  />
                  Ja
                </Box>
                <Box
                  component="label"
                  sx={{ display: 'flex', alignItems: 'center' }}
                >
                  <input
                    type="radio"
                    onBlur={handleFormSubmit}
                    name="addMore"
                    value="nej"
                    checked={addMoreMode === false}
                    onChange={() => handleAddMoreRadioChange('nej')}
                    style={{ marginRight: '8px' }}
                  />
                  Nej
                </Box>
              </Box>
            </>
          )}
        </Box>
      ))}
      {addMoreMode === true && (
        <Box
          component="button"
          onClick={handleAddNewSet}
          sx={{
            mt: 2,
            py: 1.5,
            px: 2,
            backgroundColor: DS.deepTeal,
            color: '#FFFFFF',
            borderRadius: '6px',
            width: '100%',
            border: 'none',
            cursor: 'pointer',
            fontFamily: DS.fontBody,
            fontSize: '16px',
          }}
        >
          Lägg till ytterligare förebyggande läkemedel
        </Box>
      )}
    </Box>
  );
};

export default MultiplePreviousPreventiveMedications;
